import {ApiBooking} from '@/model/Booking';
import Picklist, {ApiPicklist, DetailPicklistFilters, PicklistFilters} from '@/model/Picklist';
import {CombinedApiResponse, doApiRequest, Method, Module} from '..';
import {parseGenericFilters} from '../meta/filters';
import {ApiComparison, ApiSortsDerived} from '../meta/odata';

export enum ResourcePicklist {
  Picklist = '/Pickliste',
}

export enum PicklistAction {
  Submit = '/action/buchen',
  Park = '/action/parken',
  Edit = '/action/bearbeiten',
  Cancel = '/action/abbrechen',
}


export interface ApiPicklistSubmitData {
  Bediener: string;
  KundenNr?: number
  Positionen: ApiBooking[];
}

export async function getPicklists(
    filters: PicklistFilters, sorts: ApiSortsDerived<ApiPicklist>,
    limit: number | null = null, skip: number = 0,
): Promise<CombinedApiResponse<Picklist[]>> {
  const convertedFilters = parseGenericFilters(filters);


  const response = await doApiRequest<ApiPicklist[]>({
    module: Module.Picklist,
    resource: ResourcePicklist.Picklist,
    method: Method.Get,
    filters: [...convertedFilters, [ApiComparison.NotEquals, {field: 'Art'}, 'Frei']],
    sorts,
    limit,
    skip,
    select: new Set(['Picklistnr', 'Erstelltam', 'Erstelltdurch', 'Status', 'Art', 'kmsbediener', 'kmsdatum', 'kmsuhrzeit', 'kmsprioritaet', 'kmsam', 'kmsdurch']),
    expand: {
      Tour: null,
      Auftraege: {
        select: new Set(['Artikelnr', 'Name', 'Gebindemenge', 'Menge', 'Belegnr']),
      },
      InterneAuftraege: {
        select: new Set(['Artikelnr', 'Name', 'Gebindemenge', 'Menge', 'Belegnr']),
      },
    },
  });

  return [response.Data.map((apiPicklist) => Picklist.fromApi(apiPicklist)), response.CountAll];
}

export async function getDetailPicklist(
    filters: DetailPicklistFilters,
): Promise<CombinedApiResponse<Picklist|null>> {
  const response = await doApiRequest<ApiPicklist>({
    module: Module.Picklist,
    resource: ResourcePicklist.Picklist,
    method: Method.Get,
    id: filters.Picklistnr,
    limit: 1,
    timeout: 30_000,
  });

  return response.Data ? [Picklist.fromApi(response.Data)] : [null];
}

export async function submitPicklist(
    picklist: Picklist,
    data: ApiPicklistSubmitData,
): Promise<CombinedApiResponse<null>> {
  await doApiRequest({
    module: Module.Picklist,
    resource: ResourcePicklist.Picklist,
    method: Method.Post,
    action: PicklistAction.Submit,
    id: picklist.Picklistnr,
    select: new Set(['Picklistnr']),
    data,
  });

  return [null];
}

export async function createPicklist(
    data: ApiPicklistSubmitData,
): Promise<CombinedApiResponse<null>> {
  await doApiRequest({
    module: Module.Picklist,
    resource: ResourcePicklist.Picklist,
    method: Method.Post,
    action: undefined, // create
    select: new Set(['Picklistnr']),
    data,
  });

  return [null];
}

export async function parkPicklist(
    picklist: Picklist,
    data: ApiPicklistSubmitData,
): Promise<CombinedApiResponse<null>> {
  await doApiRequest({
    module: Module.Picklist,
    resource: ResourcePicklist.Picklist,
    method: Method.Post,
    action: PicklistAction.Park,
    id: picklist.Picklistnr,
    select: new Set(['Picklistnr']),
    data,
  });

  return [null];
}


export async function editPicklist(
    picklist: Picklist,
): Promise<CombinedApiResponse<Picklist|null>> {
  const response = await doApiRequest({
    module: Module.Picklist,
    resource: ResourcePicklist.Picklist,
    method: Method.Post,
    action: PicklistAction.Edit,
    id: picklist.Picklistnr,
    select: new Set(['Picklistnr', 'Status']),
  });

  return response.Data ? [Picklist.fromApi(response.Data, false)] : [null];
}


export async function cancelPicklist(
    picklist: Picklist,
): Promise<CombinedApiResponse<Picklist|null>> {
  const response = await doApiRequest({
    module: Module.Picklist,
    resource: ResourcePicklist.Picklist,
    method: Method.Post,
    action: PicklistAction.Cancel,
    id: picklist.Picklistnr,
    data: {
      BuchungenLoeschen: true,
    },
    select: new Set(['Picklistnr', 'Status']),
  });

  return response.Data ? [Picklist.fromApi(response.Data, false)] : [null];
}
